/**
 * The "Auction insights" upload: Google Ads' export for all campaigns as text (one Campaign column, the server
 * files each campaign under its location) and, when the file does not state it, its period. Checked before
 * sending, so a wrong file is named here rather than by the server.
 */
export const MAX_EXPORT_CHARS = 1_000_000;

/** Where staff download the all-campaigns export in Google Ads. */
export const EXPORT_STEPS =
  'In Google Ads: Reports, Predefined reports, Other, Auction insights, Search. Add the Campaign column, set the date range, then Download as .csv.';

export interface UploadDraft {
  periodStart: string;
  periodEnd: string;
  csv: string;
}

export type UploadResult =
  | {
      ok: true;
      input: { periodStart?: string; periodEnd?: string; csv: string };
    }
  | { ok: false; error: string };

const DAY = /^\d{4}-\d{2}-\d{2}$/;

export function uploadInput(draft: UploadDraft): UploadResult {
  const csv = draft.csv.trim();
  if (!csv)
    return { ok: false, error: 'Choose the auction insights export downloaded from Google Ads.' };
  if (csv.length > MAX_EXPORT_CHARS)
    return { ok: false, error: 'That file is too large for one auction insights export.' };
  if (!/display url domain/i.test(csv))
    return {
      ok: false,
      error: 'That is not an auction insights export: it has no "Display URL domain" column.',
    };
  const hasCampaignColumn = /(^|[\t,"])campaign( name)?([\t,"]|$)/im.test(
    csv.split(/\r?\n/).find((l) => /display url domain/i.test(l)) ?? '',
  );
  if (!hasCampaignColumn)
    return {
      ok: false,
      error: `That export is for one campaign: it has no Campaign column. ${EXPORT_STEPS}`,
    };
  const start = draft.periodStart.trim();
  const end = draft.periodEnd.trim();
  if ((start && !DAY.test(start)) || (end && !DAY.test(end)))
    return { ok: false, error: 'Dates are YYYY-MM-DD, or empty when the file states them.' };
  if ((start === '') !== (end === ''))
    return { ok: false, error: 'Give both the start and the end of the period, or neither.' };
  if (start && end && end < start) return { ok: false, error: 'The period ends before it starts.' };
  return {
    ok: true,
    input: {
      csv,
      ...(start ? { periodStart: start, periodEnd: end } : {}),
    },
  };
}

/** Whether another advertiser showed in this campaign's auctions: any competitor row with an impression share. */
export function competitorsShowing(
  rows: ReadonlyArray<{ isSelf: boolean; impressionSharePercent: number | null }>,
): number {
  return rows.filter((r) => !r.isSelf && (r.impressionSharePercent ?? 0) > 0).length;
}

/** One campaign of an upload, as the server filed it (backend auctionInsights.service.ts). */
export interface UploadedCampaign {
  campaign: string;
  location: string | null;
  competitors: number;
  skipped?: 'no_campaign' | 'ambiguous';
}

/** The upload's outcome in a line per group: what was filed where, and what was skipped and why. */
export function uploadSummary(campaigns: readonly UploadedCampaign[]): string[] {
  const byLocation = new Map<string, number>();
  for (const c of campaigns)
    if (c.location) byLocation.set(c.location, (byLocation.get(c.location) ?? 0) + 1);
  const lines = [...byLocation].map(
    ([location, n]) => `${location}: ${n} ${n === 1 ? 'campaign' : 'campaigns'}`,
  );
  const unknown = campaigns.filter((c) => c.skipped === 'no_campaign').map((c) => c.campaign);
  if (unknown.length > 0)
    lines.push(`Skipped, no such Google campaign in this practice: ${unknown.join(', ')}`);
  const ambiguous = campaigns.filter((c) => c.skipped === 'ambiguous').map((c) => c.campaign);
  if (ambiguous.length > 0)
    lines.push(
      `Skipped, more than one location has a campaign of this name (upload from that location): ${ambiguous.join(', ')}`,
    );
  return lines;
}
